import dayjs, { Dayjs } from 'dayjs';

export function getRoundedDifferenceQuantity(
  a: string | number | Date | Dayjs,
  b: string | number | Date | Dayjs,
  unit: dayjs.ManipulateType = 'minute'
) {
  const da = dayjs(a);
  const db = dayjs(b);
  const floatDiff = db.diff(da, unit, true);
  return Math.round(floatDiff);
}

type DivisibilityResult = {
  /** Signed number of whole milliseconds if exact, otherwise null */
  wholeMilliseconds: number | null;
  /** Signed number of whole seconds if exact, otherwise null */
  wholeSeconds: number | null;
  /** Signed number of whole minutes if exact, otherwise null */
  wholeMinutes: number | null;
  /** Signed number of whole hours if exact, otherwise null */
  wholeHours: number | null;
  /** Signed number of whole days if exact, otherwise null */
  wholeDays: number | null;
  /** Signed number of whole weeks if exact, otherwise null */
  wholeWeeks: number | null;
  /** Signed number of whole months if exact, otherwise null */
  wholeMonths: number | null;
  /** Signed number of whole years if exact (months divisible by 12), otherwise null */
  wholeYears: number | null;

  /** True when the difference is an exact whole number of milliseconds */
  isExactMilliseconds: boolean;
  /** True when the difference is an exact whole number of seconds */
  isExactSeconds: boolean;
  /** True when the difference is an exact whole number of minutes */
  isExactMinutes: boolean;
  /** True when the difference is an exact whole number of hours */
  isExactHours: boolean;
  /** True when the difference is an exact whole number of days */
  isExactDays: boolean;
  /** True when the difference is an exact whole number of weeks */
  isExactWeeks: boolean;
  /** True when the difference is an exact whole number of months */
  isExactMonths: boolean;
  /** True when the difference is an exact whole number of years */
  isExactYears: boolean;
};

/**
 * Return whether the difference between a and b is exactly N units
 * (milliseconds, seconds, minutes, hours, days, weeks, months, years).
 *
 * For months, time and day-of-month must match for an exact month
 * (e.g., 2020-01-15 12:00 -> 2020-02-15 12:00 is 1 month).
 * Years are exact only when the month count is divisible by 12.
 */
export function timeDivisibility(
  a: string | number | Date | Dayjs,
  b: string | number | Date | Dayjs,
  epsilon = 1e-8
): DivisibilityResult {
  const da = dayjs(a);
  const db = dayjs(b);

  // Generic checker
  const check = (unit: dayjs.ManipulateType) => {
    const floatDiff = db.diff(da, unit, true);
    const rounded = Math.round(floatDiff);
    const isExact = Math.abs(floatDiff - rounded) < epsilon;
    return { isExact, whole: isExact ? rounded : null };
  };

  // Simple units
  const ms = check('millisecond');
  const s = check('second');
  const min = check('minute');
  const h = check('hour');
  const d = check('day');
  const w = check('week');

  // Special handling for months
  const monthsFloat = db.diff(da, 'month', true);
  const roundedMonths = Math.round(monthsFloat);
  const isExactMonths = Math.abs(monthsFloat - roundedMonths) < epsilon;
  const wholeMonths = isExactMonths ? roundedMonths : null;

  // Special handling for years
  let isExactYears = false;
  let wholeYears: number | null = null;
  if (isExactMonths) {
    isExactYears = roundedMonths % 12 === 0;
    wholeYears = isExactYears ? roundedMonths / 12 : null;
  }

  return {
    wholeMilliseconds: ms.whole,
    wholeSeconds: s.whole,
    wholeMinutes: min.whole,
    wholeHours: h.whole,
    wholeDays: d.whole,
    wholeWeeks: w.whole,
    wholeMonths,
    wholeYears,

    isExactMilliseconds: ms.isExact,
    isExactSeconds: s.isExact,
    isExactMinutes: min.isExact,
    isExactHours: h.isExact,
    isExactDays: d.isExact,
    isExactWeeks: w.isExact,
    isExactMonths,
    isExactYears,
  };
}

export type BestUnitResult = {
  unit: dayjs.ManipulateType;
  amount: number;
  approximate: boolean;
};

/**
 * Choose the most appropriate time unit and amount to represent the duration
 * between two timestamps using a threshold-based heuristic
 */
export function chooseBestUnit(
  a: string | number | Date | Dayjs,
  b: string | number | Date | Dayjs
): BestUnitResult {
  const da = dayjs(a);
  const db = dayjs(b);
  const diffMs = db.diff(da, 'millisecond', true);

  const absMs = Math.abs(diffMs);

  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day; // approximation
  const year = 365 * day; // approximation

  let unit: dayjs.ManipulateType;
  let amount: number;

  if (absMs < second) {
    // Very short durations -> milliseconds
    unit = 'millisecond';
    amount = Math.round(diffMs);
  } else if (absMs < 2 * minute) {
    // Short durations -> seconds
    unit = 'second';
    amount = Math.round(diffMs / second);
  } else if (absMs < 2 * hour) {
    // Up to a couple of hours -> minutes
    unit = 'minute';
    amount = Math.round(diffMs / minute);
  } else if (absMs < 2 * day) {
    // Up to ~48 hours -> hours
    unit = 'hour';
    amount = Math.round(diffMs / hour);
  } else if (absMs < 14 * day) {
    // Up to two weeks -> days
    unit = 'day';
    amount = Math.round(diffMs / day);
  } else if (absMs < 10 * week) {
    // Up to ~2.5 months -> weeks
    unit = 'week';
    amount = Math.round(diffMs / week);
  } else if (absMs < 18 * month) {
    // Up to ~1.5 years -> months
    unit = 'month';
    amount = Math.round(diffMs / month);
  } else {
    // Longer durations -> years
    unit = 'year';
    amount = Math.round(diffMs / year);
  }

  return { unit, amount, approximate: true };
}
